// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SystemDocument } from '@interfaces/document.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { UserLogonState, UserTypes } from '@interfaces/user.interfaces';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';
import { Observable, forkJoin, mergeAll, take, takeWhile } from 'rxjs';
import { SystemDocumentModalComponent } from 'src/app/modals/system-document-modal/system-document-modal.component';
import { tenantIsStellantis } from 'src/app/utils/utils';
import { environment } from 'src/environments/environment';
import { PermissionsService } from '../../services/permissions.service';
import { SharedService } from '@services/shared.service';
import { ModalSize } from '@interfaces/shared.interfaces';
import { AuthService } from '../services/auth.service';

@Component({
    selector: 'system-document',
    templateUrl: './system-document.component.html',
    styleUrls: ['./system-document.component.scss'],
})
export class SystemDocumentComponent implements OnInit, OnDestroy {
    public companyImage: string = null;
    private isComponentAlive = true;
    public isLoading = false;
    public productImage: string = null;
    public requiredDocs: SystemDocument[] = [];
    public showImage = false;
    private systemDocs: SystemDocument[] = [];
    public year = new Date().getFullYear();
    public isProd: boolean = environment.production;

    constructor(
        private readonly dialog: MatDialog,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly permissionsService: PermissionsService,
        private readonly navService: NavService,
        private readonly sharedService: SharedService
    ) {}

    ngOnInit(): void {
        this.isComponentAlive = true;
        this.navService.setTitle('Agreements');

        const data = this.route.snapshot.data;
        this.requiredDocs = data.systemDocs;
        this.setImages();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    public eventHandler(event: AppEvent): void {
        switch (event.context) {
            case 'step-2-complete':
                this.isLoading = true;
                this.saveAgreements(event.data);
                break;
            case 'close':
                this.navService.accountType = null;
                this.userService.logOut();
                break;
            default:
                break;
        }
    }

    private saveAgreements(docs: SystemDocument[]): void {
        this.saveEachAgreement(docs)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                mergeAll()
            )
            .subscribe((val) => {
                if (val) {
                    this.userService
                        .getUser$()
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            take(1)
                        )
                        .subscribe(async (user) => {
                            this.userService
                                .updateLastLogon$(user, UserLogonState.SystemDocumentsAccepted)
                                .pipe(takeWhile(() => this.isComponentAlive))
                                .subscribe();

                            if (this.authService.redirectUrl) {
                                await this.router.navigate([this.authService.redirectUrl]);
                                this.authService.redirectUrl = null;
                            } else {
                                await (user.userType === UserTypes.Customer
                                    ? this.router.navigate(['/main', 'vehicles']) // buyer
                                    : this.router.navigate(this.permissionsService.routeToDealerHomePage())); // dealer
                            }
                        });
                }
            });
    }

    private saveEachAgreement(docs: SystemDocument[]): Observable<SystemDocument[][]> {
        const agreed = docs.map((agreement) => this.userService.updateRequireSystemDoc(agreement.currentSystemDocumentId));
        return forkJoin(agreed);
    }

    private setImages(): void {
        if (tenantIsStellantis()) {
            this.companyImage = '../../assets/img/stellantis-logo-white.png';
            this.productImage = '../../assets/img/ez-docs-logo-white.png';
            this.showImage = true;
        } else {
            this.companyImage = '../../assets/img/DDJ-logo-white.png';
            this.productImage = '../../assets/img/cropped-ud-logo-white.png';
        }
    }

    private async getSystemDocuments(type: number): Promise<void> {
        //Load documents only when trying to view documents and only once.
        if (this.systemDocs.length === 0) {
            this.userService
                .getHelpPageSystemDocuments()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((docs) => {
                    this.systemDocs = docs;
                    this.openModal(type);
                });
        } else {
            this.openModal(type);
        }
    }

    private openModal(docType: number): void {
        this.dialog.open(SystemDocumentModalComponent, {
            width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
            height: this.sharedService.getModalHeight(ModalSize.Medium) + 'px',
            closeOnNavigation: false,
            data: {
                modalSize: ModalSize.Medium,
                doc: this.systemDocs[docType],
            },
        });
    }

    public viewDocument(type: number): Promise<void> {
        return this.getSystemDocuments(type);
    }
}
